import React, { useEffect, useState } from 'react';
import { Navbar } from '../components/Navbar';
import { Footer } from '../components/Footer';
import { BarChart3Icon, LinkIcon, DollarSignIcon, UsersIcon, TrendingUpIcon, ArrowUpIcon, ArrowDownIcon, DownloadIcon, ExternalLinkIcon, PlusIcon, InfoIcon } from 'lucide-react';
import { AffiliateReferralLink } from '../components/affiliate/AffiliateReferralLink';
import { AffiliateNotifications } from '../components/affiliate/AffiliateNotifications';
// Empty state data for new users
const affiliateStats = {
  totalEarnings: 0,
  pendingPayment: 0,
  clicksThisMonth: 0,
  conversionsThisMonth: 0,
  conversionRate: 0,
  topReferringPages: [] as {
    url: string;
    clicks: number;
    conversions: number;
    revenue: number;
  }[],
  recentReferrals: [] as {
    date: string;
    product: string;
    commission: number;
    status: string;
  }[],
  monthlyPerformance: [] as {
    month: string;
    clicks: number;
    conversions: number;
    earnings: number;
  }[]
};
export const AffiliateDashboard = () => {
  const [scrolled, setScrolled] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');
  useEffect(() => {
    const handleScroll = () => {
      const isScrolled = window.scrollY > 50;
      if (isScrolled !== scrolled) {
        setScrolled(isScrolled);
      }
    };
    window.addEventListener('scroll', handleScroll, {
      passive: true
    });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [scrolled]);
  return <div className="w-full bg-gray-50 dark:bg-[#0A1C1A] min-h-screen" data-id="element-4426">
      <Navbar scrolled={scrolled} data-id="element-4427" />
      <main className="pt-24 pb-20" data-id="element-4428">
        <div className="container mx-auto px-4 md:px-6" data-id="element-4429">
          <div className="max-w-6xl mx-auto" data-id="element-4430">
            {/* Dashboard Header */}
            <div className="mb-8" data-id="element-4431">
              <div className="flex items-center justify-between mb-6" data-id="element-4432">
                <h1 className="text-2xl font-bold text-gray-900 dark:text-[#F8FAF9]" data-id="element-4433">
                  Affiliate Dashboard
                </h1>
                <div className="flex items-center space-x-2" data-id="element-4434">
                  <AffiliateNotifications data-id="element-4435" />
                </div>
              </div>
              <p className="text-gray-600 dark:text-[#B0C1BD]" data-id="element-4436">
                Track your referrals, earnings, and manage your affiliate links
              </p>
            </div>

            {/* Empty State Banner */}
            <div className="bg-blue-50 dark:bg-blue-900/10 border border-blue-100 dark:border-blue-900/30 rounded-xl p-5 mb-8 flex items-start" data-id="element-4437">
              <InfoIcon className="h-5 w-5 text-blue-500 dark:text-blue-400 mr-3 mt-0.5 flex-shrink-0" data-id="element-4438" />
              <div data-id="element-4439">
                <p className="text-blue-800 dark:text-blue-300 font-medium" data-id="element-4440">
                  No data yet
                </p>
                <p className="text-blue-600 dark:text-blue-400 text-sm mt-1" data-id="element-4441">
                  Start sharing your affiliate link to begin earning
                  commissions. Your stats will appear here once you have
                  activity.
                </p>
              </div>
            </div>

            {/* Stats Overview */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8" data-id="element-4442">
              <div className="bg-white dark:bg-[#162825] rounded-xl p-6 shadow-sm" data-id="element-4443">
                <div className="flex items-center justify-between mb-4" data-id="element-4444">
                  <div className="p-3 rounded-full bg-[#2DB489]/10 dark:bg-[#2DB489]/20" data-id="element-4445">
                    <DollarSignIcon className="h-6 w-6 text-[#2DB489] dark:text-[#4ED1A0]" data-id="element-4446" />
                  </div>
                </div>
                <h3 className="text-sm text-gray-500 dark:text-[#7A8F8A] mb-1" data-id="element-4447">
                  Total Earnings
                </h3>
                <p className="text-2xl font-bold text-gray-800 dark:text-[#F8FAF9]" data-id="element-4448">
                  $0.00
                </p>
                <p className="text-xs text-gray-500 dark:text-[#7A8F8A] mt-1" data-id="element-4449">
                  $0.00 pending
                </p>
              </div>
              <div className="bg-white dark:bg-[#162825] rounded-xl p-6 shadow-sm" data-id="element-4450">
                <div className="flex items-center justify-between mb-4" data-id="element-4451">
                  <div className="p-3 rounded-full bg-blue-100 dark:bg-blue-900/20" data-id="element-4452">
                    <UsersIcon className="h-6 w-6 text-blue-500 dark:text-blue-400" data-id="element-4453" />
                  </div>
                </div>
                <h3 className="text-sm text-gray-500 dark:text-[#7A8F8A] mb-1" data-id="element-4454">
                  Total Clicks
                </h3>
                <p className="text-2xl font-bold text-gray-800 dark:text-[#F8FAF9]" data-id="element-4455">
                  0
                </p>
                <p className="text-xs text-gray-500 dark:text-[#7A8F8A] mt-1" data-id="element-4456">
                  This month
                </p>
              </div>
              <div className="bg-white dark:bg-[#162825] rounded-xl p-6 shadow-sm" data-id="element-4457">
                <div className="flex items-center justify-between mb-4" data-id="element-4458">
                  <div className="p-3 rounded-full bg-purple-100 dark:bg-purple-900/20" data-id="element-4459">
                    <TrendingUpIcon className="h-6 w-6 text-purple-500 dark:text-purple-400" data-id="element-4460" />
                  </div>
                </div>
                <h3 className="text-sm text-gray-500 dark:text-[#7A8F8A] mb-1" data-id="element-4461">
                  Conversions
                </h3>
                <p className="text-2xl font-bold text-gray-800 dark:text-[#F8FAF9]" data-id="element-4462">
                  0
                </p>
                <p className="text-xs text-gray-500 dark:text-[#7A8F8A] mt-1" data-id="element-4463">
                  This month
                </p>
              </div>
              <div className="bg-white dark:bg-[#162825] rounded-xl p-6 shadow-sm" data-id="element-4464">
                <div className="flex items-center justify-between mb-4" data-id="element-4465">
                  <div className="p-3 rounded-full bg-orange-100 dark:bg-orange-900/20" data-id="element-4466">
                    <BarChart3Icon className="h-6 w-6 text-orange-500 dark:text-orange-400" data-id="element-4467" />
                  </div>
                </div>
                <h3 className="text-sm text-gray-500 dark:text-[#7A8F8A] mb-1" data-id="element-4468">
                  Conversion Rate
                </h3>
                <p className="text-2xl font-bold text-gray-800 dark:text-[#F8FAF9]" data-id="element-4469">
                  0%
                </p>
                <p className="text-xs text-gray-500 dark:text-[#7A8F8A] mt-1" data-id="element-4470">
                  Clicks to sales
                </p>
              </div>
            </div>

            {/* Dashboard Tabs */}
            <div className="mb-8 border-b border-gray-200 dark:border-[#1A3330]" data-id="element-4471">
              <nav className="flex space-x-8" data-id="element-4472">
                <button className={`py-4 px-1 border-b-2 font-medium text-sm ${activeTab === 'overview' ? 'border-[#2DB489] dark:border-[#4ED1A0] text-[#2DB489] dark:text-[#4ED1A0]' : 'border-transparent text-gray-500 dark:text-[#7A8F8A] hover:text-gray-700 dark:hover:text-[#B0C1BD]'} transition-colors duration-200`} onClick={() => setActiveTab('overview')} data-id="element-4473">
                  <BarChart3Icon className="h-4 w-4 inline mr-2" data-id="element-4474" />
                  Overview
                </button>
                <button className={`py-4 px-1 border-b-2 font-medium text-sm ${activeTab === 'links' ? 'border-[#2DB489] dark:border-[#4ED1A0] text-[#2DB489] dark:text-[#4ED1A0]' : 'border-transparent text-gray-500 dark:text-[#7A8F8A] hover:text-gray-700 dark:hover:text-[#B0C1BD]'} transition-colors duration-200`} onClick={() => setActiveTab('links')} data-id="element-4475">
                  <LinkIcon className="h-4 w-4 inline mr-2" data-id="element-4476" />
                  My Links
                </button>
                <button className={`py-4 px-1 border-b-2 font-medium text-sm ${activeTab === 'payments' ? 'border-[#2DB489] dark:border-[#4ED1A0] text-[#2DB489] dark:text-[#4ED1A0]' : 'border-transparent text-gray-500 dark:text-[#7A8F8A] hover:text-gray-700 dark:hover:text-[#B0C1BD]'} transition-colors duration-200`} onClick={() => setActiveTab('payments')} data-id="element-4477">
                  <DollarSignIcon className="h-4 w-4 inline mr-2" data-id="element-4478" />
                  Payments
                </button>
              </nav>
            </div>

            {/* Tab Content */}
            {activeTab === 'overview' && <div className="space-y-8" data-id="element-4479">
                {/* Performance Chart */}
                <div className="bg-white dark:bg-[#162825] rounded-xl p-6 shadow-sm" data-id="element-4480">
                  <h2 className="text-xl font-semibold text-gray-800 dark:text-[#F8FAF9] mb-6" data-id="element-4481">
                    Performance Over Time
                  </h2>
                  <div className="h-64 w-full flex items-center justify-center" data-id="element-4482">
                    <div className="text-center" data-id="element-4483">
                      <BarChart3Icon className="h-12 w-12 text-gray-300 dark:text-[#1A3330] mx-auto mb-3" data-id="element-4484" />
                      <p className="text-gray-500 dark:text-[#7A8F8A] font-medium" data-id="element-4485">
                        No activity yet
                      </p>
                      <p className="text-gray-400 dark:text-[#5A6F6A] text-sm mt-1" data-id="element-4486">
                        Your performance data will appear here once you start
                        generating referrals.
                      </p>
                    </div>
                  </div>
                </div>

                {/* Top Referring Pages */}
                <div className="bg-white dark:bg-[#162825] rounded-xl p-6 shadow-sm" data-id="element-4487">
                  <h2 className="text-xl font-semibold text-gray-800 dark:text-[#F8FAF9] mb-6" data-id="element-4488">
                    Top Referring Pages
                  </h2>
                  <div className="overflow-x-auto" data-id="element-4489">
                    <table className="w-full" data-id="element-4490">
                      <thead data-id="element-4491">
                        <tr className="border-b border-gray-200 dark:border-[#1A3330]" data-id="element-4492">
                          <th className="text-left py-3 px-4 text-sm font-semibold text-gray-600 dark:text-[#B0C1BD]" data-id="element-4493">
                            Page
                          </th>
                          <th className="text-right py-3 px-4 text-sm font-semibold text-gray-600 dark:text-[#B0C1BD]" data-id="element-4494">
                            Clicks
                          </th>
                          <th className="text-right py-3 px-4 text-sm font-semibold text-gray-600 dark:text-[#B0C1BD]" data-id="element-4495">
                            Conversions
                          </th>
                          <th className="text-right py-3 px-4 text-sm font-semibold text-gray-600 dark:text-[#B0C1BD]" data-id="element-4496">
                            Revenue
                          </th>
                        </tr>
                      </thead>
                      <tbody data-id="element-4497">
                        <tr data-id="element-4498">
                          <td colSpan={4} className="py-8 text-center" data-id="element-4499">
                            <ExternalLinkIcon className="h-8 w-8 text-gray-300 dark:text-[#1A3330] mx-auto mb-2" data-id="element-4500" />
                            <p className="text-gray-500 dark:text-[#7A8F8A] text-sm" data-id="element-4501">
                              No referring pages yet
                            </p>
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>

                {/* Recent Referrals */}
                <div className="bg-white dark:bg-[#162825] rounded-xl p-6 shadow-sm" data-id="element-4502">
                  <h2 className="text-xl font-semibold text-gray-800 dark:text-[#F8FAF9] mb-6" data-id="element-4503">
                    Recent Referrals
                  </h2>
                  <div className="overflow-x-auto" data-id="element-4504">
                    <table className="w-full" data-id="element-4505">
                      <thead data-id="element-4506">
                        <tr className="border-b border-gray-200 dark:border-[#1A3330]" data-id="element-4507">
                          <th className="text-left py-3 px-4 text-sm font-semibold text-gray-600 dark:text-[#B0C1BD]" data-id="element-4508">
                            Date
                          </th>
                          <th className="text-left py-3 px-4 text-sm font-semibold text-gray-600 dark:text-[#B0C1BD]" data-id="element-4509">
                            Product
                          </th>
                          <th className="text-right py-3 px-4 text-sm font-semibold text-gray-600 dark:text-[#B0C1BD]" data-id="element-4510">
                            Commission
                          </th>
                          <th className="text-right py-3 px-4 text-sm font-semibold text-gray-600 dark:text-[#B0C1BD]" data-id="element-4511">
                            Status
                          </th>
                        </tr>
                      </thead>
                      <tbody data-id="element-4512">
                        <tr data-id="element-4513">
                          <td colSpan={4} className="py-8 text-center" data-id="element-4514">
                            <DollarSignIcon className="h-8 w-8 text-gray-300 dark:text-[#1A3330] mx-auto mb-2" data-id="element-4515" />
                            <p className="text-gray-500 dark:text-[#7A8F8A] text-sm" data-id="element-4516">
                              No referrals yet
                            </p>
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>}

            {activeTab === 'links' && <div className="space-y-8" data-id="element-4517">
                <div className="bg-white dark:bg-[#162825] rounded-xl p-6 shadow-sm" data-id="element-4518">
                  <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6" data-id="element-4519">
                    <h2 className="text-xl font-semibold text-gray-800 dark:text-[#F8FAF9]" data-id="element-4520">
                      My Affiliate Links
                    </h2>
                    <button className="inline-flex items-center bg-[#2DB489] dark:bg-[#4ED1A0] text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-[#2DB489]/90 dark:hover:bg-[#4ED1A0]/90 transition-colors" data-id="element-4521">
                      <PlusIcon className="h-4 w-4 mr-2" data-id="element-4522" />
                      Create New Link
                    </button>
                  </div>
                  <div className="text-center py-8" data-id="element-4523">
                    <LinkIcon className="h-12 w-12 text-gray-300 dark:text-[#1A3330] mx-auto mb-3" data-id="element-4524" />
                    <p className="text-gray-500 dark:text-[#7A8F8A] font-medium" data-id="element-4525">
                      No affiliate links yet
                    </p>
                    <p className="text-gray-400 dark:text-[#5A6F6A] text-sm mt-1" data-id="element-4526">
                      Create your first affiliate link to start sharing and
                      earning commissions.
                    </p>
                  </div>
                </div>
                <div className="bg-white dark:bg-[#162825] rounded-xl p-6 shadow-sm" data-id="element-4527">
                  <h2 className="text-xl font-semibold text-gray-800 dark:text-[#F8FAF9] mb-6" data-id="element-4528">
                    Promotional Materials
                  </h2>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-4529">
                    <div className="border border-gray-200 dark:border-[#1A3330] rounded-lg p-4" data-id="element-4530">
                      <div className="aspect-video bg-gray-100 dark:bg-[#1A3330] rounded-md mb-3 flex items-center justify-center" data-id="element-4531">
                        <span className="text-gray-500 dark:text-[#7A8F8A] text-sm" data-id="element-4532">
                          Banner 300x250
                        </span>
                      </div>
                      <p className="text-sm text-gray-800 dark:text-[#F8FAF9] mb-3" data-id="element-4533">
                        Sidebar Banner (300x250)
                      </p>
                      <button className="w-full bg-gray-100 dark:bg-[#1A3330] text-gray-800 dark:text-[#B0C1BD] px-3 py-2 rounded text-sm hover:bg-gray-200 dark:hover:bg-[#214541] transition-colors" data-id="element-4534">
                        Get Code
                      </button>
                    </div>
                    <div className="border border-gray-200 dark:border-[#1A3330] rounded-lg p-4" data-id="element-4535">
                      <div className="aspect-video bg-gray-100 dark:bg-[#1A3330] rounded-md mb-3 flex items-center justify-center" data-id="element-4536">
                        <span className="text-gray-500 dark:text-[#7A8F8A] text-sm" data-id="element-4537">
                          Banner 728x90
                        </span>
                      </div>
                      <p className="text-sm text-gray-800 dark:text-[#F8FAF9] mb-3" data-id="element-4538">
                        Leaderboard Banner (728x90)
                      </p>
                      <button className="w-full bg-gray-100 dark:bg-[#1A3330] text-gray-800 dark:text-[#B0C1BD] px-3 py-2 rounded text-sm hover:bg-gray-200 dark:hover:bg-[#214541] transition-colors" data-id="element-4539">
                        Get Code
                      </button>
                    </div>
                    <div className="border border-gray-200 dark:border-[#1A3330] rounded-lg p-4" data-id="element-4540">
                      <div className="aspect-video bg-gray-100 dark:bg-[#1A3330] rounded-md mb-3 flex items-center justify-center" data-id="element-4541">
                        <span className="text-gray-500 dark:text-[#7A8F8A] text-sm" data-id="element-4542">
                          Email Template
                        </span>
                      </div>
                      <p className="text-sm text-gray-800 dark:text-[#F8FAF9] mb-3" data-id="element-4543">
                        Product Recommendation Email
                      </p>
                      <button className="w-full bg-gray-100 dark:bg-[#1A3330] text-gray-800 dark:text-[#B0C1BD] px-3 py-2 rounded text-sm hover:bg-gray-200 dark:hover:bg-[#214541] transition-colors" data-id="element-4544">
                        Get Template
                      </button>
                    </div>
                  </div>
                </div>
              </div>}

            {activeTab === 'payments' && <div className="bg-white dark:bg-[#162825] rounded-xl p-6 shadow-sm" data-id="element-4545">
                <h2 className="text-xl font-semibold text-gray-800 dark:text-[#F8FAF9] mb-6" data-id="element-4546">
                  Payment History
                </h2>
                <div className="overflow-x-auto" data-id="element-4547">
                  <table className="w-full" data-id="element-4548">
                    <thead data-id="element-4549">
                      <tr className="border-b border-gray-200 dark:border-[#1A3330]" data-id="element-4550">
                        <th className="text-left py-3 px-4 text-sm font-semibold text-gray-600 dark:text-[#B0C1BD]" data-id="element-4551">
                          Date
                        </th>
                        <th className="text-left py-3 px-4 text-sm font-semibold text-gray-600 dark:text-[#B0C1BD]" data-id="element-4552">
                          Reference
                        </th>
                        <th className="text-right py-3 px-4 text-sm font-semibold text-gray-600 dark:text-[#B0C1BD]" data-id="element-4553">
                          Amount
                        </th>
                        <th className="text-right py-3 px-4 text-sm font-semibold text-gray-600 dark:text-[#B0C1BD]" data-id="element-4554">
                          Status
                        </th>
                      </tr>
                    </thead>
                    <tbody data-id="element-4555">
                      <tr data-id="element-4556">
                        <td colSpan={4} className="py-8 text-center" data-id="element-4557">
                          <DollarSignIcon className="h-8 w-8 text-gray-300 dark:text-[#1A3330] mx-auto mb-2" data-id="element-4558" />
                          <p className="text-gray-500 dark:text-[#7A8F8A] text-sm" data-id="element-4559">
                            No payments yet
                          </p>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
                <div className="mt-8 p-4 bg-gray-50 dark:bg-[#1A3330] rounded-lg" data-id="element-4560">
                  <h3 className="text-lg font-semibold text-gray-800 dark:text-[#F8FAF9] mb-3" data-id="element-4561">
                    Payment Settings
                  </h3>
                  <div className="space-y-4" data-id="element-4562">
                    <div className="flex items-center justify-between" data-id="element-4563">
                      <div data-id="element-4564">
                        <p className="font-medium text-gray-800 dark:text-[#F8FAF9]" data-id="element-4565">
                          Current Payment Method
                        </p>
                        <p className="text-sm text-gray-500 dark:text-[#7A8F8A]" data-id="element-4566">
                          Not configured
                        </p>
                      </div>
                      <button className="text-[#2DB489] dark:text-[#4ED1A0] hover:underline text-sm" data-id="element-4567">
                        Set Up
                      </button>
                    </div>
                    <div className="flex items-center justify-between" data-id="element-4568">
                      <div data-id="element-4569">
                        <p className="font-medium text-gray-800 dark:text-[#F8FAF9]" data-id="element-4570">
                          Payment Threshold
                        </p>
                        <p className="text-sm text-gray-500 dark:text-[#7A8F8A]" data-id="element-4571">
                          $50.00 minimum for payout
                        </p>
                      </div>
                      <button className="text-[#2DB489] dark:text-[#4ED1A0] hover:underline text-sm" data-id="element-4572">
                        Edit
                      </button>
                    </div>
                    <div className="flex items-center justify-between" data-id="element-4573">
                      <div data-id="element-4574">
                        <p className="font-medium text-gray-800 dark:text-[#F8FAF9]" data-id="element-4575">
                          Payment Schedule
                        </p>
                        <p className="text-sm text-gray-500 dark:text-[#7A8F8A]" data-id="element-4576">
                          Monthly (Last day of month)
                        </p>
                      </div>
                      <button className="text-[#2DB489] dark:text-[#4ED1A0] hover:underline text-sm" data-id="element-4577">
                        Edit
                      </button>
                    </div>
                  </div>
                </div>
              </div>}
          </div>
        </div>
      </main>
      <Footer data-id="element-4578" />
    </div>;
};